/* Copyright (c) 2026 Xperi. All Rights Reserved. */

export const SIGN_IN_HELP_LINK: string = "tivo.com/help/signin";

/** Title of the overlay shown while a retryable service call is retrying. */
export const ALERT_TITLE_CONNECTION_ERROR: string = "Connection Error";

/** Dismiss reason used when a retried call finally succeeds. */
export const ALERT_REASON_RETRY_RECOVERED: string = "retry-recovered";
/** The retry overlay came down because the error turned out to be terminal. */
export const ALERT_REASON_RETRY_TERMINAL: string = "retry-terminal";

/** Owner tag for the sign-in connection-error / background-retry overlay. */
export const OWNER_SIGNIN_RETRY: string = "signin-connection-retry";

/**
 * Action id for "Sign In Again" on the sign-in connection-error overlay: ends
 * the attempt that is retrying and returns to the credential form. Distinct
 * from the terminal alert's `sign_in_again`, which only acknowledges an
 * attempt that has already ended.
 */
export const SIGN_IN_AGAIN_RETRY_ACTION_ID: string =
  "signin-retry-sign-in-again";

/**
 * Owner tag for the terminal sign-in error alert — the one the retry ladder
 * hands over to once it gives up (Retry / Sign In Again / Need Help / Exit).
 *
 * Both sign-in owners are what `selectIsSignInErrorOverlayActive` matches on,
 * so an overlay raised by any other subsystem (TSRI recovery, post-sign-in
 * startup, Need Help, the password-reset dialogs) keeps today's navigation
 * behaviour untouched.
 */
export const OWNER_SIGNIN_ERROR: string = "signin-error";

/** QR code rendering parameters for the DBS license-plate code. */
export const QR_SCALE: number = 8;
export const QR_BORDER: number = 2;

/**
 * Rejection messages produced by the DBS polling loop. The polling thunk
 * rejects with these and the DBS reducers branch on them, so both sides must
 * read the same constant rather than duplicating the literal.
 */
export const DBS_ERROR_POLLING_CANCELLED: string = "Polling cancelled";
export const DBS_ERROR_LP_EXPIRED: string = "License plate expired";

/** HTTP status codes the sign-in flows branch on. */
export const HTTP_STATUS_BAD_REQUEST: number = 400;
export const HTTP_STATUS_UNAUTHORIZED: number = 401;
export const HTTP_STATUS_FORBIDDEN: number = 403;
export const HTTP_STATUS_NOT_FOUND: number = 404;
export const HTTP_STATUS_CONFLICT: number = 409;
export const HTTP_STATUS_PRECONDITION_FAILED: number = 412;
/** Lowest status treated as a server-side failure. */
export const HTTP_STATUS_SERVER_ERROR_MIN: number = 500;

/** Substrings matched against error messages during classification. */
export const ERROR_FRAGMENT_TIMEOUT_LOWER: string = "timeout";
export const ERROR_FRAGMENT_TIMEOUT_UPPER: string = "Timeout";
export const ERROR_FRAGMENT_ABORT: string = "abort";
export const ERROR_FRAGMENT_CANCEL: string = "cancel";
export const ERROR_FRAGMENT_NETWORK_ERROR: string = "Network Error";
export const ERROR_FRAGMENT_ERR_NETWORK: string = "ERR_NETWORK";

/**
 * Skew window applied when checking domain-token expiry. A token whose
 * expiration falls within this window from now is treated as expired so
 * we refresh proactively rather than letting an in-flight call fail.
 *
 * Defined by the auth package and re-exported here: TokenManager applies the
 * same window, and two independent copies could drift into disagreeing about
 * whether a given token is still usable.
 */
export { TOKEN_EXPIRY_SKEW_MS } from "../../model/auth";
